import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { axiosErrorFrom, airServerErrorFrom } from '@/lib/error-handling';
import { validateRouteRole } from '@/lib/routing';
import { isCoach, isEdgeCoach } from '@airreading/common';
import { createFileRoute } from '@tanstack/react-router';
import { paymentsTabs } from './-payments-tabs';
import { CardTitle } from '@/components/ui/card';
import { GuidelineTable } from './-components/guideline-table';
import { guidelineText } from './-components/guideline-text';
import { getCoachUpcomingPayments } from '@/lib/api-calls';
import { useQuery } from '@tanstack/react-query';
import { EntityTable } from '@/components/table/entity-table';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CoachPaymentReportDTO, PayCycleDTO } from '@airreading/common/data';
import { TableErrorState } from '@/components/table/table-error-state';

export const Route = createFileRoute('/_layout/payments/guideline')({
  beforeLoad: validateRouteRole((user) => isCoach(user) && !isEdgeCoach(user)),
  component: Page,
});

function Page() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load coach payments. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <CoachPaymentsPage />
    </InfoPageSuspenseBoundary>
  );
}

const columnHelper = createColumnHelper<CoachPaymentReportDTO>();

const columns = [
  columnHelper.accessor('payDate', {
    header: 'Pay initiation date',
    enableSorting: false,
  }),
  columnHelper.accessor('payCycleId', {
    header: 'Period',
    cell: ({ getValue }) => {
      const payCycleId = getValue();
      const period = PayCycleDTO.fromPayPeriodId(payCycleId).payPeriodInterval;
      return period;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('depositPostedDate', {
    header: 'Delivery (1-3 business days)',
    enableSorting: false,
  }),
];

function CoachPaymentsPage() {
  const { data: upcomingPayments, isPending, refetch, error } = useQuery(getCoachUpcomingPayments());

  const table = useReactTable({
    data: upcomingPayments || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <InfoPage headerText="Payments" tabs={paymentsTabs('Guideline')}>
        <CardTitle className="text-xl font-bold flex items-center mb-4">Upcoming payments</CardTitle>
        <EntityTable
          table={table}
          showPagination={false}
          isLoading={isPending}
          hasError={!!error}
          errorState={<TableErrorState refetchAction={refetch} />}
          hideEndMessage
          emptyState={'No payments found'}
          emptyStateClassName="translate-y-0"
        />

        <CardTitle className="text-xl font-bold mb-4">Payment breakdown</CardTitle>
        <section className="mb-4">
          {guidelineText.map((section) => (
            <GuidelineTable key={section.type} {...section} />
          ))}
        </section>
        <p>
          Additional earning opportunities including supporting curriculum and operations at Air Reading, referral
          incentives, and more.
        </p>
      </InfoPage>
    </div>
  );
}
